import React from "react"
import { Link } from "gatsby"
import GuideLayout from "../../components/guide/guide-layout"
import { findGuide } from "../../data/guides"

const guide = findGuide("office-pod-planning-permission-uk")

const Page = () => (
  <GuideLayout guide={guide}>
    <p>
      Short answer: a phone booth or meeting pod placed inside an existing office does not need planning permission. A pod in the garden of a house usually does not either, provided it stays within the permitted development limits. A structure outside a commercial building almost always does. The detail below explains why, and what you should check instead of planning permission in each case.
    </p>

    <div className="callout">
      <p>
        This guide describes the position in England for general information. Wales, Scotland and Northern Ireland have their own rules, and every site has its own history. It is not legal advice. If anything here is close to your situation, confirm it with your local planning authority or a planning professional before you commit.
      </p>
    </div>

    <h2>1. A booth inside your office</h2>
    <p>
      Planning permission is needed for "development". Under section 55 of the Town and Country Planning Act 1990, works that only affect the interior of a building are not development. A freestanding booth like <Link to="/vibra-one/">Vibra One</Link> is furniture, not building work: it is delivered in parts, assembled on the floor, plugged into a socket and can be moved again. There is no application to make.
    </p>
    <p>That does not mean there is nothing to check. Three things usually matter more than planning:</p>
    <ul>
      <li>
        <strong>Your lease.</strong> Most commercial leases contain an alterations clause. Freestanding furniture is normally outside it, but anything fixed to the floor, walls or ceiling, or wired into the building's electrics rather than plugged in, can count as an alteration that needs the landlord's written consent. Check before you order, not after.
      </li>
      <li>
        <strong>Fire safety.</strong> The Regulatory Reform (Fire Safety) Order 2005 makes the "responsible person" keep the fire risk assessment up to date. A booth changes the room: it adds an enclosed space, uses floor area that may have been an escape route, and introduces new materials. Update the assessment, keep the required widths to exits, and ask the manufacturer for the fire performance of the wall linings and acoustic felt.
      </li>
      <li>
        <strong>Listed buildings.</strong> Listed building consent is about works that affect the special character of the building. A booth that simply stands on the floor does not need it. Fixing a booth into historic fabric, or cutting into a floor or ceiling for services, might. When in doubt, ask the council's conservation officer; the question is quick to answer.
      </li>
    </ul>
    <p>
      Ventilation and electrics are Building Regulations matters rather than planning ones. A booth with its own fan and a plug-in supply, like ours, does not trigger a Building Regulations application. If your fit-out contractor is adding new circuits or altering the ventilation system to serve a pod, that work must be done by a competent person and certified in the normal way.
    </p>

    <h2>2. A garden office pod at home</h2>
    <p>
      This is where the questions really start, because a garden pod is a building and building one is development. For most houses in England the answer is still no application, because outbuildings are covered by permitted development rights under Class E of Part 1, Schedule 2 of the General Permitted Development Order 2015. The rights come with conditions, and every one of them has to be met:
    </p>
    <ul>
      <li>The pod must be for a purpose incidental to the enjoyment of the house. A home office for the people who live there qualifies. Separate living accommodation does not.</li>
      <li>It cannot sit forward of the principal elevation of the house.</li>
      <li>It must be single storey, with eaves no higher than 2.5 metres.</li>
      <li>Overall height is limited to 4 metres for a dual-pitched roof, 3 metres for any other roof, and 2.5 metres if any part of the pod is within 2 metres of a boundary.</li>
      <li>Outbuildings, together with any extensions, may not cover more than 50 percent of the land around the original house.</li>
      <li>No verandas, balconies or raised platforms.</li>
    </ul>
    <p>Then there are the situations where the rights are reduced or missing entirely:</p>
    <ul>
      <li>
        <strong>Flats and maisonettes</strong> have no Class E rights at all. A pod in the shared garden of a converted house needs planning permission.
      </li>
      <li>
        <strong>Conservation areas, National Parks, Areas of Outstanding Natural Beauty and the Broads</strong> (together called Article 2(3) land) remove the right to put an outbuilding at the side of the house, and limit the size of anything more than 20 metres from the house.
      </li>
      <li>
        <strong>Listed buildings</strong> have no Class E rights, so a pod in the garden of a listed house needs both planning permission and, potentially, listed building consent.
      </li>
      <li>
        <strong>Removed rights.</strong> Some newer estates had permitted development rights taken away by a condition on the original planning permission, and some councils have made Article 4 directions that do the same for a whole area. Your council's planning search will tell you.
      </li>
    </ul>
    <p>
      There is also the question of use. Working alone from a garden office is fine. If the pod becomes a place where staff come to work, clients visit every day and deliveries arrive, the council can decide that the character of the use has changed enough to be a material change of use of the property, which needs permission in its own right. The test is about impact on the neighbours, not about whether money is earned.
    </p>

    <h3>Worth doing even when no permission is needed</h3>
    <p>
      If your pod relies on permitted development, consider applying for a lawful development certificate. It costs a fraction of a planning application, it settles the question formally, and it saves an awkward conversation when you sell the house. Magna Studio, a London architectural design practice we know, has a clear explanation of when a certificate is the better route in their guide on <a href="https://magnastudio.co.uk/guides/planning-permission-vs-lawful-development-certificate">planning permission versus a lawful development certificate</a>, and their <a href="https://magnastudio.co.uk/guides/permitted-development-rights-london">permitted development rights guide</a> walks through the Class E limits with the London-specific traps. Both are worth ten minutes before you pour a base.
    </p>

    <h2>3. A pod outside a commercial building</h2>
    <p>
      Putting a booth or meeting pod in a courtyard, a car park or on a roof terrace of an office building is different again. It is an external structure on commercial land, and the generous householder rights above do not apply. There is a limited permitted development right for small buildings within the curtilage of an office (Class A of Part 7 of the GPDO), but it comes with tight size and location conditions and is switched off on Article 2(3) land. In practice, most external pods at commercial premises need a planning application, and the landlord's consent as well.
    </p>
    <p>
      If this is your plan, get a planning professional to look at the site before you buy the pod. A short piece of pre-application advice is cheap compared with taking a structure down.
    </p>

    <h2>Quick reference</h2>
    <table>
      <thead>
        <tr>
          <th>Situation</th>
          <th>Planning permission?</th>
          <th>Check instead</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Freestanding booth inside an office</td>
          <td>No</td>
          <td>Lease alterations clause, fire risk assessment, listed building status</td>
        </tr>
        <tr>
          <td>Garden pod at a house, within Class E limits</td>
          <td>No, permitted development</td>
          <td>Height and boundary limits, 50 percent rule, Article 4 directions; consider a lawful development certificate</td>
        </tr>
        <tr>
          <td>Garden pod at a flat, listed house or over the limits</td>
          <td>Yes</td>
          <td>Full application, listed building consent if applicable</td>
        </tr>
        <tr>
          <td>Pod outside a commercial building</td>
          <td>Usually yes</td>
          <td>Pre-application advice, landlord consent</td>
        </tr>
      </tbody>
    </table>

    <h2>What we do to make this simple</h2>
    <p>
      Vibra One is designed as furniture. It is freestanding and runs from a standard socket, so nothing has to be cut, drilled or wired into your building. That keeps it out of the alterations clause in most leases and away from listed building questions. If you need the technical data for a fire risk assessment, ask and we will send it.
    </p>
    <p className="note">
      Sources: Town and Country Planning Act 1990 s.55; Town and Country Planning (General Permitted Development) (England) Order 2015, Schedule 2 Part 1 Class E and Part 7 Class A; Regulatory Reform (Fire Safety) Order 2005; Planning Portal guidance on outbuildings. Last reviewed September 2026.
    </p>
  </GuideLayout>
)

export default Page
